import { useCallback, useRef, type MouseEvent, type PointerEvent } from "react";
import type { ImageCanvasObject } from "@refinement-canvas/domain";
import { resolveObjectCursor, useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";
import { PENDING_ATTACHMENT_SRC, isPendingAttachmentSrc } from "../attachment-placeholder";
import { useImageLoadState } from "./use-image-load-state";

interface Props {
  readonly object: ImageCanvasObject;
}

// 画像オブジェクト。PNG / JPG / GIF / WebP / SVG に対応する。
// SVG は <img> 経由でベクター描画され、拡大しても滲まない（<img> 内ではスクリプトは
// 実行されないため XSS も発生しない）。
// ビューポート外の画像は CanvasObjectsLayer のカリングで描画・取得を抑える。
// 描画対象は eager で取得し、パン・ズーム後にブラウザー側の遅延判定を重ねない。
// クリックで選択、ドラッグで移動できる。
//
// 表示は 3 層構造：
//  1. LQIP 背景レイヤ: ストアにキャプチャされた低品質プレースホルダ（32px の透過対応画像）を、
//     枠サイズ（元画像サイズ）まで拡大し blur をかけたぼかしプレビューとして表示する。
//     本体 URL が読み込み中／失敗のあいだだけ見える状態にし、本体表示後は描画しない
//     （透過画像で本体背後の LQIP が透けて見えたり、ドラッグ時に取り残されるのを防ぐ）。
//  2. 本体画像レイヤ: object.src を表示する。読み込み完了 & エラーなしまで opacity:0 にして、
//     裏の LQIP が見える状態にする（仮表示中＝src が data: URL のときも同様）。
//  3. オーバーレイ: 仮表示中／本体読み込み中はスピナー。エラー時は LQIP を残しつつ、
//     LQIP が無い場合の画像アイコンと、画像だけを再取得できる操作を表示する。
export function ImageObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLImageElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));
  const readOnly = useCanvasStore((s) => s.readOnly);
  // LQIP（低品質プレースホルダ）の取得元は 2 系統あり、優先順は以下:
  //   1. 永続化された object.placeholderSrc（サーバに保存されているので、リロード後・別セッション・
  //      新規貼付した画像を含むあらゆる参加者でフォールバックが効く）
  //   2. session 寿命のクライアントキャッシュ attachmentLqips（object.placeholderSrc が未設定の
  //      レガシーオブジェクトや、旧クライアントが create した場合の互換用フォールバック）
  // どちらも data: URL（透明 1x1 プレースホルダ以外）の前提。
  const lqipFromStore = useCanvasDocStore((s) => s.attachmentLqips[object.id]);
  const lqipSource = object.placeholderSrc ?? lqipFromStore;

  // src が data: URL（=仮表示）かどうか。確定済みの blob:（本人フル画質）/ https URL では false。
  const isPending = isPendingAttachmentSrc(object.src);
  // 本体画像（公開 URL / blob:）の読み込み完了/失敗状態を、対象 src と紐付けて保持する。
  // useEffect で src 変更時に false へリセットする方式は、ブラウザキャッシュ済み画像や小さな
  // data: URL で onLoad が passive effect より先に発火する順序になり、リセットで取りこぼした
  // 結果 showMain が false のまま固まる退行を招きうる。src に紐付けた状態を使い、購読時には
  // DOM の完了状態も確認して、先行した load を取りこぼさないようにする。
  const { retry, status } = useImageLoadState(ref, object.src, isPending);
  const stopRetryPointer = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    // ステージのネイティブ pointerdown（配置・描画）より前の capture 段階で止める。
    // React の bubble ではステージへ先に届いてしまう。
    event.stopPropagation();
  }, []);
  const retryImage = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      retry();
    },
    [retry]
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref as unknown as React.RefObject<HTMLElement | null>,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  // LQIP として有用なものがあるか（透明 1x1 プレースホルダは背景として無意味なので除外）。
  const hasUsefulLqip = lqipSource !== undefined && lqipSource !== PENDING_ATTACHMENT_SRC;
  // 現在の object.src と紐付いた最新の読み込み状態だけを採用する（古い src の load/error は無視）。
  const mainLoaded = status === "loaded";
  const errored = status === "errored";
  // 本体画像を最終表示してよいか（仮表示でない & 読み込み完了 & エラーなし）。
  const showMain = !isPending && mainLoaded && !errored;
  // スピナーを出すか（仮表示中、または本体を読み込み中で未失敗）。
  const showSpinner = (isPending || !mainLoaded) && !errored;
  // LQIP フォールバックが無い場合のみ、エラーアイコンの中立な枠を表示する。
  const showErrorGlyph = errored && !hasUsefulLqip;

  // 共通の枠スタイル（座標・サイズ・回転・zOrder）。LQIP 背景／本体／オーバーレイで揃える。
  const frame: React.CSSProperties = {
    position: "absolute",
    left: `${object.x}px`,
    top: `${object.y}px`,
    width: `${object.w}px`,
    height: `${object.h}px`,
    zIndex: object.zOrder,
    transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
    transformOrigin: "center",
  };

  // 選択時のアウトラインは「ユーザに見えている要素」に付ける。本体が見えているなら本体、
  // 本体が非表示で LQIP だけが見えているなら LQIP、どちらも見えない（仮表示／エラー時で LQIP も
  // 無い場合）はオーバーレイ div に付ける（複数要素には乗せない）。
  // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする。
  const SELECTION_OUTLINE = "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)";
  const mainOutline = isSelected && showMain ? SELECTION_OUTLINE : "none";
  const lqipOutline = isSelected && !showMain && hasUsefulLqip ? SELECTION_OUTLINE : "none";
  const overlayOutline = isSelected && !showMain && !hasUsefulLqip ? SELECTION_OUTLINE : "none";

  return (
    <>
      {hasUsefulLqip && !showMain && (
        <img
          src={lqipSource}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            ...frame,
            objectFit: "contain",
            // 32px 由来のジャギーを隠すためぼかし、object-fit: contain で枠サイズへ拡大する。
            filter: "blur(8px)",
            userSelect: "none",
            pointerEvents: "none",
            outline: lqipOutline,
          }}
        />
      )}
      <img
        ref={ref}
        data-testid={`canvas-object-${object.id}`}
        data-object-type="image"
        data-pending={isPending ? "true" : undefined}
        src={object.src}
        alt={object.alt ?? ""}
        loading="eager"
        decoding="async"
        style={{
          ...frame,
          objectFit: "contain",
          userSelect: "none",
          pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
          // 参照専用では動かせないため default。配置ツール・ペン描画の選択中は新規作成（crosshair）であることを示し、移動と誤認させない。
          cursor: resolveObjectCursor({ readOnly, activeTool }),
          outline: mainOutline,
          opacity: showMain ? 1 : 0,
        }}
        draggable={false}
      />
      {(showSpinner || errored) && (
        <div
          className="attachment-pending-overlay"
          data-variant={showSpinner ? "loading" : "error"}
          data-has-lqip={hasUsefulLqip ? "true" : undefined}
          style={{
            ...frame,
            pointerEvents: "none",
            outline: overlayOutline,
          }}
        >
          {showSpinner ? (
            <span className="attachment-pending-spinner" aria-hidden="true" />
          ) : (
            <div style={{ display: "grid", justifyItems: "center", gap: "0.5rem" }}>
              {showErrorGlyph && (
                <span className="attachment-pending-glyph" aria-hidden="true">
                  🖼
                </span>
              )}
              <button
                type="button"
                className="loading-overlay-reload-button"
                onPointerDownCapture={stopRetryPointer}
                onClick={retryImage}
                style={{
                  background: "var(--color-surface)",
                  color: "var(--color-text)",
                  pointerEvents: "auto",
                }}
              >
                画像を再読み込み
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
